import { MutationDefinition } from '@reduxjs/toolkit/query'

import { baseQueryWithNotifications } from '@redux/api/config'
import { fetchContextMenuConfigsByIds } from '@redux/api/helpers/fetchContextMenuConfigsByIds'
import { fetchDisplayRulesByIds } from '@redux/api/helpers/fetchDisplayRulesByIds'

import { enrichViewActionWithTargetColumns } from '@helpers'

import { URLS } from '@constants'
import { ActionRow, ConfiguredColumn, GETEntity } from '@types'

import { fetchTooltipConfigsFromActions } from './fetchTooltipConfigsFromActions'

type FetchTimelineConfigFn = MutationDefinition<
  { id: string | number; instanceId?: string },
  typeof baseQueryWithNotifications,
  string,
  GETEntity,
  'commonApi'
>['queryFn']

export const fetchEnrichedEntityConfigById: NonNullable<FetchTimelineConfigFn> = async (
  props,
  api,
  _,
  baseQuery
) => {
  const queryResult = await baseQuery({
    url: URLS.FRONT_CFG + `/view/id/${props.id}`,
    method: 'GET',
  })

  if (queryResult.error) {
    return { error: queryResult.error }
  }

  const configData = queryResult.data as GETEntity

  const contextMenuIds = Array.from(
    new Set(
      configData.actions
        ?.filter(action => action.contextMenuId)
        .map(action => action.contextMenuId as number)
    )
  )

  const displayRuleIds = Array.from(
    new Set(
      configData.columns
        ?.filter(column => column.useDisplayRule && column.displayRuleId)
        .map(column => column.displayRuleId as number)
    )
  )

  const [tooltips, contextMenus, displayRules] = await Promise.all([
    fetchTooltipConfigsFromActions({ actions: configData.actions || [], api }),
    fetchContextMenuConfigsByIds({ contextMenuIds, api }),
    fetchDisplayRulesByIds({ ids: displayRuleIds, api }),
  ])

  const enrichActions = (actions?: ActionRow[]) => {
    return actions?.map(action => {
      return enrichViewActionWithTargetColumns(
        {
          ...action,
          tooltip: tooltips.has(action.objectTooltipId)
            ? tooltips.get(action.objectTooltipId)
            : undefined,
          contextMenu: contextMenus.has(action.contextMenuId)
            ? contextMenus.get(action.contextMenuId)
            : undefined,
        },
        configData.columns
      )
    })
  }

  const enrichColumns = (columns?: ConfiguredColumn[]) => {
    return columns?.map(column => {
      return {
        ...column,
        displayRule: displayRules.get(column.displayRuleId),
      }
    })
  }

  return {
    data: {
      ...configData,
      actions: enrichActions(configData.actions),
      columns: enrichColumns(configData.columns),
    },
  }
}
